"use strict";
/**
 * WsUseCaseAlignment (Public/Embedded View)
 *
 * Visualizes the alignment between a strategy and a use case through
 * business metrics: Strategy columns → Metrics → Use Case.
 *
 * Thin orchestrator that wires together:
 *  - {@link useStrategies}: strategies list + loading flag.
 *  - {@link useStrategyAlignment}: alignment fetch state machine
 *    (`idle | loading | success | error`) with debounce + telemetry +
 *    response validation.
 *  - {@link AlignmentHeader} / {@link AlignmentPlaceholder} /
 *    {@link AlignmentLoadingOverlay} / {@link AlignmentGraph}: presentation,
 *    each `React.memo`'d so unrelated re-renders are cheap.
 *
 * @param {WsUseCaseAlignmentProps} props - Component props.
 * @returns {JSX.Element} Rendered Use Case Alignment view.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WsUseCaseAlignment = void 0;
var react_1 = __importStar(require("react"));
var MessageBar_1 = require("office-ui-fabric-react/lib/MessageBar");
require("./styles/use-case-alignment.scss");
var components_1 = require("./components");
var hooks_1 = require("../shared/hooks");
var hooks_2 = require("./hooks");
var utils_1 = require("./utils");
var WsUseCaseAlignment = function (props) {
    var _a;
    var _b = hooks_1.useStrategies(), strategies = _b.data, isLoadingStrategies = _b.isLoading, strategiesError = _b.error;
    var _c = react_1.useState(''), selectedStrategyId = _c[0], setSelectedStrategyId = _c[1];
    // Sanitize the host-supplied identifier at the trust boundary: defends
    // against log injection, accidental template misuse, and pathological
    // server-side processing of attacker-controlled URL params.
    var selectedUseCaseId = react_1.useMemo(function () { return utils_1.sanitizeOpaqueId(props.useCaseId); }, [props.useCaseId]);
    var alignment = hooks_2.useStrategyAlignment((_a = props.workflowId) !== null && _a !== void 0 ? _a : '', selectedStrategyId, selectedUseCaseId);
    var strategyOptions = react_1.useMemo(function () { return utils_1.buildStrategyOptions(strategies); }, [strategies]);
    var hasStrategies = strategies.length > 0;
    var isLoadingAlignment = alignment.status === 'loading';
    var showUseCaseDropdown = !props.useCaseId;
    var canShowPlaceholder = !isLoadingStrategies && hasStrategies;
    var hasNoAffectedNodes = alignment.status === 'success' && alignment.data.affectedNodes.length === 0;
    var handleStrategyChange = react_1.useCallback(function (strategyId, option) {
        var _a;
        var selectedStrategy = strategies.find(function (strategy) { return String(strategy.id) === strategyId; }) || null;
        console.log('[WsUseCaseAlignment] Strategy selected for alignment fetch', {
            workflowId: (_a = props.workflowId) !== null && _a !== void 0 ? _a : '',
            useCaseId: selectedUseCaseId,
            selectedStrategyId: strategyId,
            selectedOption: option
                ? {
                    key: String(option.key),
                    text: option.text,
                    data: option.data,
                }
                : null,
            selectedStrategy: selectedStrategy,
        });
        setSelectedStrategyId(strategyId);
    }, [props.workflowId, selectedUseCaseId, strategies]);
    var renderBody = function () {
        switch (alignment.status) {
            case 'success':
                return (react_1.default.createElement(components_1.AlignmentGraph, { alignmentData: alignment.data, isLoading: false, useCaseMetricNames: props.useCaseMetricNames }));
            case 'loading':
                return react_1.default.createElement(components_1.AlignmentLoadingOverlay, null);
            case 'error':
                return canShowPlaceholder ? (react_1.default.createElement(components_1.AlignmentPlaceholder, { text: utils_1.resolvePlaceholderText(true) })) : null;
            case 'idle':
            default:
                return canShowPlaceholder ? (react_1.default.createElement(components_1.AlignmentPlaceholder, { text: utils_1.resolvePlaceholderText(false) })) : null;
        }
    };
    return (react_1.default.createElement("div", { className: "ws-use-case-alignment" },
        alignment.status === 'error' && (react_1.default.createElement(MessageBar_1.MessageBar, { messageBarType: MessageBar_1.MessageBarType.error, onDismiss: alignment.clearError, dismissButtonAriaLabel: "Close" }, alignment.error)),
        hasNoAffectedNodes && (react_1.default.createElement(MessageBar_1.MessageBar, { messageBarType: MessageBar_1.MessageBarType.info }, "This use case does not contribute any data to the selected strategy, so no nodes are highlighted below.")),
        react_1.default.createElement(components_1.AlignmentHeader, { strategyOptions: strategyOptions, selectedStrategyId: selectedStrategyId, selectedUseCaseId: selectedUseCaseId, isLoadingStrategies: isLoadingStrategies, isLoadingAlignment: isLoadingAlignment, hasStrategies: hasStrategies, 
            // TODO(ux): replace the disabled "Select Use Case" dropdown that
            // appears when this is true with an inline hint — a permanently
            // disabled empty dropdown is misleading affordance.
            showUseCaseDropdown: showUseCaseDropdown, onStrategyChange: handleStrategyChange }),
        !isLoadingStrategies && strategiesError && (react_1.default.createElement(MessageBar_1.MessageBar, { messageBarType: MessageBar_1.MessageBarType.error }, strategiesError)),
        !isLoadingStrategies && !strategiesError && !hasStrategies && (react_1.default.createElement(MessageBar_1.MessageBar, { messageBarType: MessageBar_1.MessageBarType.info }, "No strategies have been configured yet. Please ask an administrator to add strategies in the Strategy Builder.")),
        renderBody()));
};
exports.WsUseCaseAlignment = WsUseCaseAlignment;
